import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { CONCENTRATION, CSS_CERTIFIED, EXPORT_ENUMS, SEX } from '@/constants';
import { ColDef, ValueFormatterParams } from '@/grid/types';
import { concentrationFormatter } from '@/grid/value-formatters/concentrationFormatter';
import { cssCertifiedFormatter } from '@/grid/value-formatters/cssCertifiedFormatter';
import { dateFormatter } from '@/grid/value-formatters/dateFormatter';
import { exportFormatter } from '@/grid/value-formatters/exportFormatter';
import { percentFormatter } from '@/grid/value-formatters/percentFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { isSireInactive } from '@/utils/sire';

const blankFilterLabel = '(Blanks)';

const formatSex = (
  sex?: number | string | null,
  useConventionalLabel = false,
) => {
  const numericSex = typeof sex === 'string' ? Number(sex) : sex;

  if (numericSex === SEX.Female) return 'Female';
  if (numericSex === SEX.Male) return 'Male';
  if (numericSex === SEX.Unknown) {
    return useConventionalLabel ? 'Conventional' : 'Unknown';
  }
  return 'Unknown';
};

type SemenLookupRow = Record<string, unknown> & {
  sire?: { stateCode?: number | null } | null;
};

export function useColumnDefs() {
  const { t } = useTranslation();
  const isAdvancedSemenDataSourceV2Enabled = useFeatureFlag(
    'Advanced_Semen_Lookup_V2',
  );
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const isBlockInactiveSiresEnabled = useFeatureFlag('block_inactive_sires');

  return useMemo<ColDef<SemenLookupRow>[]>(() => {
    const nameColumnDef: ColDef<SemenLookupRow> = {
      headerName: 'Name',
      field: 'name',
      filter: 'agTextColumnFilter',
      ...(isBlockInactiveSiresEnabled
        ? {
            valueFormatter(
              params: ValueFormatterParams<SemenLookupRow, string | null>,
            ) {
              return isSireInactive(params.data?.sire)
                ? `${params.value ?? ''} (${t('sire.inactive')})`
                : (params.value ?? '');
            },
            cellClassRules: {
              'text-red-600'(params: { data?: SemenLookupRow }) {
                return isSireInactive(params.data?.sire);
              },
            },
          }
        : {}),
    };

    if (isAdvancedSemenDataSourceV2Enabled) {
      return [
        nameColumnDef,
        {
          headerName: 'Cane',
          field: 'cane',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Col Code',
          field: 'colCode',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'NAAB',
          field: 'naab',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Quantity',
          field: 'quantity',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'TEDR',
          field: 'totalEmbryoDevelopmentRate',
          valueFormatter: percentFormatter,
          filter: false,
        },
        {
          headerName: 'QEDR',
          field: 'qualityEmbryoDevelopmentRate',
          valueFormatter: percentFormatter,
          filter: false,
        },
        {
          headerName: 'Sexed',
          field: 'sexed',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [true, false, null],
            valueFormatter(params: { value?: unknown }) {
              if (params.value == null) return blankFilterLabel;
              return params.value ? 'Yes' : 'No';
            },
          },
        },
        {
          headerName: 'Sex',
          field: 'sex',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [SEX.Female, SEX.Male, SEX.Unknown],
            valueFormatter(params: { value?: number | string | null }) {
              return formatSex(params.value, isSemenConventionalTypeEnabled);
            },
          },
          valueFormatter(
            params: ValueFormatterParams<
              SemenLookupRow,
              number | string | null
            >,
          ) {
            return formatSex(params.value, isSemenConventionalTypeEnabled);
          },
        },
        {
          headerName: 'Semen Type',
          field: 'semenType',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Ranch ID (Ranch)',
          field: 'ownerCode',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Ranch',
          field: 'ownerName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sold To',
          field: 'soldTo',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Secondary Owner',
          field: 'secondaryOwner',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Shipment',
          field: 'shipmentName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Storage Location',
          field: 'storageLocation.name',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Site (Storage Location)',
          field: 'semenLocationSite',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Date Received',
          field: 'dateReceived',
          valueFormatter: dateFormatter,
          filter: 'agDateColumnFilter',
        },
        {
          headerName: 'Export',
          field: 'export',
          valueFormatter: exportFormatter,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: Object.values(EXPORT_ENUMS),
            valueFormatter: (params: { value?: unknown }) =>
              Object.entries(EXPORT_ENUMS).find(
                ([, val]) => val === params.value,
              )?.[0] ?? '',
          },
        },
        {
          headerName: 'Straw Stamped Certified',
          field: 'cssCertified',
          valueFormatter: cssCertifiedFormatter,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [CSS_CERTIFIED.No, CSS_CERTIFIED.Yes],
            valueFormatter(params: { value?: unknown }) {
              return params.value === CSS_CERTIFIED.No
                ? 'No'
                : params.value === CSS_CERTIFIED.Yes
                  ? 'Yes'
                  : 'Unknown';
            },
          },
        },
        {
          headerName: 'Cost',
          field: 'cost',
          filter: 'agNumberColumnFilter',
        },
        {
          headerName: 'Concentration',
          field: 'concentration',
          valueFormatter: concentrationFormatter,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: [...Object.values(CONCENTRATION), null],
            valueFormatter(params: { value?: unknown }) {
              if (params.value == null) return blankFilterLabel;
              return concentrationFormatter(params);
            },
          },
        },
        {
          headerName: 'Semen Comment',
          field: 'comment',
          filter: 'agTextColumnFilter',
        },
      ];
    }

    return [
      nameColumnDef,
      {
        headerName: 'Cane',
        field: 'cane',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Col Code',
        field: 'colCode',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'NAAB',
        field: 'naab',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Quantity',
        field: 'quantity',
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'TEDR',
        field: 'totalEmbryoDevelopmentRate',
        valueFormatter: percentFormatter,
        filter: false,
      },
      {
        headerName: 'QEDR',
        field: 'qualityEmbryoDevelopmentRate',
        valueFormatter: percentFormatter,
        filter: false,
      },
      {
        headerName: 'Sexed',
        field: 'sexed',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false, null],
          valueFormatter(params: { value?: unknown }) {
            if (params.value == null) return blankFilterLabel;
            return params.value ? 'Yes' : 'No';
          },
        },
      },
      {
        headerName: 'Sex',
        field: 'sex',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [SEX.Female, SEX.Male, SEX.Unknown],
          valueFormatter(params: { value?: number | string | null }) {
            return formatSex(params.value, isSemenConventionalTypeEnabled);
          },
        },
        valueFormatter(
          params: ValueFormatterParams<SemenLookupRow, number | string | null>,
        ) {
          return formatSex(params.value, isSemenConventionalTypeEnabled);
        },
      },
      {
        headerName: 'Semen Type',
        field: 'semenType',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Ranch ID (Ranch)',
        field: 'owner.code',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Ranch',
        field: 'owner.name',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Sold To',
        field: 'soldTo',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Secondary Owner',
        field: 'secondaryOwner',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Shipment',
        field: 'shipment.shipName',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Need By Date (Shipment)',
        field: 'shipment.needByDate',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
      },
      {
        headerName: 'Storage Location',
        field: 'storageLocation.name',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Site (Storage Location)',
        field: 'semenLocationSite',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Date Received',
        field: 'dateReceived',
        valueFormatter: dateFormatter,
        filter: 'agDateColumnFilter',
      },
      {
        headerName: 'Export',
        field: 'export',
        valueFormatter: exportFormatter,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Straw Stamped Certified',
        field: 'cssCertified',
        valueFormatter: cssCertifiedFormatter,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [CSS_CERTIFIED.No, CSS_CERTIFIED.Yes],
          valueFormatter(params: { value?: unknown }) {
            return params.value === CSS_CERTIFIED.No
              ? 'No'
              : params.value === CSS_CERTIFIED.Yes
                ? 'Yes'
                : 'Unknown';
          },
        },
      },
      {
        headerName: 'Cost',
        field: 'cost',
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Concentration',
        field: 'concentration',
        valueFormatter: concentrationFormatter,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [...Object.values(CONCENTRATION), null],
          valueFormatter(params: { value?: unknown }) {
            if (params.value == null) return blankFilterLabel;
            return concentrationFormatter(params);
          },
        },
      },
      {
        headerName: 'Semen Comment',
        field: 'comment',
        filter: 'agTextColumnFilter',
      },
    ];
  }, [
    isAdvancedSemenDataSourceV2Enabled,
    isSemenConventionalTypeEnabled,
    isBlockInactiveSiresEnabled,
    t,
  ]);
}
